import {NavList} from '@primer/react'
import type {ReactNode} from 'react'
import {memo} from 'react'
import {SyncIcon} from '@primer/octicons-react'
import styles from './NavigationItem.module.css'

export interface NavigationItemProps {
  href: string
  ariaCurrent?: 'page' | undefined
  testSelector?: string
  turbo?: boolean
  showRerunButton?: boolean
  rerunDialogId?: string
  jobId?: number
  leadingVisual?: ReactNode
  children: ReactNode
}

/**
 * Custom NavList.Item component that supports show-on-hover trailing actions.
 * This replicates the behavior of Actions::WorkflowRuns::NavigationItemComponent
 * which adds the "ActionListItem--trailingActionHover" class for show-on-hover.
 *
 * Wrapped in React.memo to prevent unnecessary re-renders when parent updates
 * but this item's props haven't changed.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const NavigationItem = memo(function NavigationItem({
  href,
  ariaCurrent,
  testSelector,
  turbo = true,
  showRerunButton,
  rerunDialogId,
  jobId,
  leadingVisual,
  children,
}: NavigationItemProps) {
  // Build data attributes for the item
  const dataAttrs: Record<string, string> = {}
  if (testSelector) {
    dataAttrs['data-test-selector'] = testSelector
  }
  if (!turbo) {
    dataAttrs['data-turbo'] = 'false'
  }

  // Build data attributes for the rerun button - will be spread on the underlying IconButton
  const rerunButtonAttrs: Record<string, string | undefined> = {
    'data-show-dialog-id': rerunDialogId,
    'data-job-id': jobId?.toString(),
    'data-action': 'mouseenter:run-summary#mouseEnterRerun mouseleave:run-summary#mouseLeaveRerun',
  }

  return (
    <NavList.Item
      href={href}
      aria-current={ariaCurrent}
      className={showRerunButton ? styles.itemWithTrailingAction : undefined}
      {...dataAttrs}
    >
      {leadingVisual ? <NavList.LeadingVisual>{leadingVisual}</NavList.LeadingVisual> : null}
      {children}
      {showRerunButton && rerunDialogId && (
        <NavList.TrailingAction
          icon={SyncIcon}
          label="Re-run job"
          className={styles.rerunButton}
          {...rerunButtonAttrs}
        />
      )}
    </NavList.Item>
  )
})
